// src/components/Main.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MpSdk } from '@mp/bundle-sdk/sdk';
import {
  Frame,
  GetSDK,
  initComponents,
  orientedBoxType,
  slotType,
  sdkKey,
} from '@mp/common';

import type { AppState } from '../AppState';
import { slots, items } from '../AppState';
import { ItemList } from './ItemList';
import type { ItemDesc } from '../types';
import { SceneLoader } from '../SceneLoader';

// 表示したい Matterport スペース
const SHOWCASE_SID = 'n2nnuJjMYvS';
// ローカル JSON シーン（3dobjsel）のキー
const LOCAL_SCENE_ID = 'byRyoMatsuo';

type SlotBundle = {
  node: MpSdk.Scene.INode;
  slot: MpSdk.Scene.IComponent;
  model: MpSdk.Scene.IComponent;
  box?: MpSdk.Scene.IComponent;
};

type Props = {
  appState: AppState;
};

// ========= 設定 =========

// センサー領域の可視化（調整用）
const SENSOR_DEBUG = false;

// orientedBox（スロット箱）を表示したいなら true（通常 false）
const SHOW_SLOT_BOX = false;

// sources.json を fetch するなら true（404がうるさければ false のまま）
const LOAD_SOURCES_JSON = false;

// ========= Trigger sources =========
type TriggerSourceConfig =
  | {
      type: 'BOX';
      center: { x: number; y: number; z: number };
      size: { x: number; y: number; z: number };
      orientation?: { yaw: number; pitch: number; roll: number };
      userData?: Record<string, any>;
    }
  | {
      type: 'SPHERE';
      origin: { x: number; y: number; z: number };
      radius: number;
      userData?: Record<string, any>;
    }
  | {
      type: 'CYLINDER';
      basePoint: { x: number; y: number; z: number };
      radius: number;
      height: number;
      userData?: Record<string, any>;
    };

const DEFAULT_TRIGGER_SOURCES: TriggerSourceConfig[] = [
  {
    type: 'BOX',
    center: { x: -13, y: 1.2, z: -3 },
    size: { x: 5.5, y: 2.5, z: 27 },
    orientation: { yaw: 0, pitch: 0, roll: 0 },
    userData: { id: 'UI_TRIGGER' },
  },
];

async function loadTriggerSources(): Promise<TriggerSourceConfig[]> {
  if (!LOAD_SOURCES_JSON) return DEFAULT_TRIGGER_SOURCES;

  try {
    const res = await fetch('./sources.json', { cache: 'no-store' });
    if (!res.ok) throw new Error(`sources.json not found (${res.status})`);
    const json = await res.json();

    if (Array.isArray(json)) return json as TriggerSourceConfig[];
    if (json && Array.isArray(json.sources)) return json.sources as TriggerSourceConfig[];
  } catch {
    // ignore and fallback
  }
  return DEFAULT_TRIGGER_SOURCES;
}

// ========= helper（Map / Object 両対応） =========
const collectionToList = (collection: any): any[] => {
  if (!collection) return [];
  if (typeof collection.values === 'function') return Array.from(collection.values());
  return Object.values(collection);
};

export function Main({ appState }: Props) {
  void appState;

  const [sdk, setSdk] = useState<MpSdk | null>(null);

  // スロット（1個だけ使う）
  const [active, setActive] = useState<SlotBundle | null>(null);
  const sceneObjectRef = useRef<MpSdk.Scene.IObject | null>(null);
  const slotsRef = useRef<SlotBundle[]>([]);

  // エリア内判定
  const [inArea, setInArea] = useState(false);
  const inAreaRef = useRef(false);

  // ===== Defurnish（家具撤去）用 =====
  const baseViewRef = useRef<any>(null);
  const defurnishViewRef = useRef<any>(null);
  const switchingViewRef = useRef(false);

  // “いま defurnish をONにしたいか” の状態（自動で戻さない）
  const defurnishEnabledRef = useRef(false);

  // showcase iframe url
  const src = useMemo(() => {
    const params = new URLSearchParams(window.location.search);

    if (!params.get('m')) params.set('m', SHOWCASE_SID);
    if (!params.get('play')) params.set('play', '1');
    if (!params.get('qs')) params.set('qs', '0');
    if (!params.get('sr')) params.set('sr', '-.15');
    if (!params.get('ss')) params.set('ss', '0');

    if (!params.get('applicationKey')) params.set('applicationKey', sdkKey);

    return `./bundle/showcase.html?${params.toString()}`;
  }, []);

  // ===== orientedBox の表示制御（見えなくする） =====
  const applyBoxStyle = useCallback((box: MpSdk.Scene.IComponent, visible: boolean) => {
    const inputs = box.inputs as any;

    if (!SHOW_SLOT_BOX || !visible) {
      inputs.opacity = 0.0;
      inputs.lineOpacity = 0.0;
      return;
    }

    inputs.color = 0xffffff;
    inputs.opacity = 0.04;
    inputs.lineOpacity = 0.4;
  }, []);

  // ===== View切替時に“その場に留まる”ための安全ラッパー =====
  const setViewActivePreservePose = useCallback(
    async (view: any) => {
      if (!sdk || !view) return;

      // 切替連打防止
      if (switchingViewRef.current) return;
      switchingViewRef.current = true;

      try {
        const _sdk: any = sdk as any;

        // 現在カメラ姿勢を保存（取れない環境もあるので try/catch）
        let pose: any = null;
        try {
          pose = await _sdk.Camera?.getPose?.();
        } catch {
          pose = null;
        }

        const sweepId = pose?.sweep;
        const rotation = pose?.rotation;

        // View 切替（returnToStart=false）
        await view.setActive(false);

        // 可能なら、切替後に元の sweep / rotation に戻す（＝リスポーン感の抑制）
        if (sweepId && _sdk.Sweep?.moveTo) {
          // waitUntil があれば少し待つ（無くてもOK）
          try {
            await _sdk.Sweep?.current?.waitUntil?.((cur: any) => {
              const id = cur?.id ?? cur?.sid;
              return typeof id === 'string' && id.length > 0;
            });
          } catch {
            // ignore
          }

          const INSTANT =
            _sdk.Sweep?.Transition?.INSTANT ??
            _sdk.Camera?.TransitionType?.INSTANT ??
            0;

          try {
            await _sdk.Sweep.moveTo(sweepId, { rotation, transition: INSTANT });
          } catch {
            // ignore
          }
        }
      } catch (e) {
        console.error('[ViewSwitch] failed', e);
      } finally {
        switchingViewRef.current = false;
      }
    },
    [sdk],
  );

  const setDefurnish = useCallback(
    async (enabled: boolean) => {
      defurnishEnabledRef.current = enabled;
      if (!sdk) return;

      const def = defurnishViewRef.current;
      const base = baseViewRef.current;

      if (enabled) {
        if (!def) {
          console.warn(
            '[Defurnish] viewtype.defurnish が View.views に見つかりません（Space側で未提供/権限/ロールアウトの可能性）',
          );
          return;
        }
        await setViewActivePreservePose(def);
        return;
      }

      // OFF は「なし」を選んだ時だけ行う（エリア出入りでは戻さない）
      if (!base) return;
      await setViewActivePreservePose(base);
    },
    [sdk, setViewActivePreservePose],
  );

  // ===== SDK 初期化 =====
  useEffect(() => {
    let mounted = true;

    (async () => {
      const _sdk = await GetSDK('sdk-iframe', sdkKey);
      await initComponents(_sdk);

      await _sdk.Scene.configure((renderer: any, three: any) => {
        renderer.useLegacyLights = false;
        renderer.outputEncoding = three.sRGBEncoding;
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.bias = 0.0001;
        renderer.shadowMap.type = three.PCFSoftShadowMap;
      });

      const [obj] = await _sdk.Scene.createObjects(1);
      sceneObjectRef.current = obj;

      if (mounted) setSdk(_sdk);
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // ===== View一覧から base / defurnish を拾う（“拾うだけ”。自動で切替はしない） =====
  useEffect(() => {
    if (!sdk) return undefined;

    const viewsObs = (sdk as any).View?.views;
    if (!viewsObs?.subscribe) {
      console.warn('[View] sdk.View.views が見つかりません（SDK/Showcase側が未対応の可能性）');
      return undefined;
    }

    const sub = viewsObs.subscribe({
      onCollectionUpdated: (collection: any) => {
        const list = collectionToList(collection);

        // defurnish
        defurnishViewRef.current =
          list.find((v) => v?.type === 'viewtype.defurnish') ?? null;

        // base は “最初に見つかった” 非defurnishを保持（切替の度に更新しない）
        if (!baseViewRef.current) {
          baseViewRef.current =
            list.find((v) => v?.active && v?.type !== 'viewtype.defurnish') ??
            list.find((v) => v?.type === 'viewtype.layeredbase') ??
            list.find((v) => v?.type === 'viewtype.base') ??
            null;
        }
      },
    });

    return () => sub?.cancel?.();
  }, [sdk]);

  // ===== Sensor（エリア判定） =====
  useEffect(() => {
    if (!sdk) return undefined;

    let cancelled = false;
    let sensor: any = null;
    let sub: any = null;

    (async () => {
      try {
        sensor = await (sdk as any).Sensor.createSensor(
          (sdk as any).Sensor.SensorType.CAMERA,
        );
        sensor.showDebug?.(SENSOR_DEBUG);

        const configs = await loadTriggerSources();
        const createdSources: any[] = [];

        for (const cfg of configs) {
          if (cfg.type === 'BOX') {
            const src = await (sdk as any).Sensor.createSource(
              (sdk as any).Sensor.SourceType.BOX,
              {
                center: cfg.center,
                size: cfg.size,
                orientation: cfg.orientation ?? { yaw: 0, pitch: 0, roll: 0 },
                userData: cfg.userData ?? { id: 'UI_TRIGGER' },
              },
            );
            createdSources.push(src);
          } else if (cfg.type === 'SPHERE') {
            const src = await (sdk as any).Sensor.createSource(
              (sdk as any).Sensor.SourceType.SPHERE,
              {
                origin: cfg.origin,
                radius: cfg.radius,
                userData: cfg.userData ?? { id: 'UI_TRIGGER' },
              },
            );
            createdSources.push(src);
          } else if (cfg.type === 'CYLINDER') {
            const src = await (sdk as any).Sensor.createSource(
              (sdk as any).Sensor.SourceType.CYLINDER,
              {
                basePoint: cfg.basePoint,
                radius: cfg.radius,
                height: cfg.height,
                userData: cfg.userData ?? { id: 'UI_TRIGGER' },
              },
            );
            createdSources.push(src);
          }
        }

        sensor.addSource?.(...createdSources);

        sub = sensor.readings.subscribe({
          onCollectionUpdated: (sourceCollection: any) => {
            const readings = collectionToList(sourceCollection);
            const anyInRange = readings.some((r: any) => r?.inRange === true);

            inAreaRef.current = anyInRange;
            if (!cancelled) setInArea(anyInRange);
          },
        });
      } catch (e) {
        console.error('Sensor init failed', e);
      }
    })();

    return () => {
      cancelled = true;
      sub?.cancel?.();
      try {
        sensor?.showDebug?.(false);
        sensor?.dispose?.();
        sensor?.destroy?.();
      } catch {
        // ignore
      }
      sensor = null;
    };
  }, [sdk]);

  // エリア出入り：UI制御 + スロット自動選択
  // ★重要：エリア外になっても DEFURNISH は “戻さない”
  useEffect(() => {
    if (!inArea) {
      setActive(null);
      return;
    }

    // エリア内：1スロットだけ使うので自動で active をセット
    if (!active) {
      const first = slotsRef.current[0] ?? null;
      if (first) setActive(first);
    }
  }, [inArea, active]);

  // ===== ローカルシーン読み込み（slot, model, box） =====
  const loadedOnceRef = useRef(false);

  useEffect(() => {
    if (!sdk) return undefined;

    // React18 dev(StrictMode) の二重実行で2回ロードされるのを抑止
    if (loadedOnceRef.current) return undefined;
    loadedOnceRef.current = true;

    let disposed = false;
    const bundles: SlotBundle[] = [];

    const visit = (node: MpSdk.Scene.INode): boolean => {
      let slot: MpSdk.Scene.IComponent | undefined;
      let model: MpSdk.Scene.IComponent | undefined;
      let box: MpSdk.Scene.IComponent | undefined;

      for (const comp of node.componentIterator()) {
        if (comp.componentType === slotType) slot = comp;
        else if ((comp as any).componentType === 'mp.gltfLoader') model = comp;
        else if (comp.componentType === orientedBoxType) box = comp;
      }

      if (slot && model) bundles.push({ node, slot, model, box });
      return true;
    };

    const loader = new SceneLoader(sdk);

    loader
      .load(LOCAL_SCENE_ID, visit)
      .then((sceneObject) => {
        if (disposed) {
          sceneObject.stop?.();
          return;
        }

        sceneObjectRef.current = sceneObject;
        slotsRef.current = bundles;

        // orientedBox 初期化（見えない設定）
        for (const binding of bundles) {
          const box = binding.box;
          if (!box) continue;
          applyBoxStyle(box, inAreaRef.current);
        }

        // ロード完了後、すでにエリア内なら active を補完
        if (inAreaRef.current && bundles[0]) {
          setActive((prev) => prev ?? bundles[0]);
        }
      })
      .catch((err) => {
        console.error('Failed to load local scene', err);
      });

    return () => {
      disposed = true;
      if (sceneObjectRef.current) {
        sceneObjectRef.current.stop?.();
        sceneObjectRef.current = null;
      }
      // HMR時に再ロードしたいなら false に戻してもいいけど、
      // まずは安定優先で戻さない
    };
  }, [sdk, applyBoxStyle]);

  // ===== アイテム選択：CG表示 + Defurnish 発火だけ =====
  const handleItemPick = useCallback(
    (item: ItemDesc) => {
      if (!inAreaRef.current) return;

      const target = active ?? slotsRef.current[0] ?? null;
      if (!target) return;

      const url = String((item as any).url ?? '').trim();

      // CG表示（slot差し替え）
      (target.slot.inputs as any).model = url;

      const inputs = target.model.inputs as any;
      if (item.position) inputs.localPosition = item.position;
      if (item.rotation) inputs.localRotation = item.rotation;
      if (item.scale) inputs.localScale = item.scale;

      // 「なし」判定
      const label = String((item as any).name ?? (item as any).label ?? '');
      const isNone =
        url.length === 0 ||
        url.toLowerCase() === 'null' || 
        label.includes('なし') ||
        label.toLowerCase() === 'none' ||
        label.toLowerCase().includes('none');
const shouldDefurnish = !isNone && (label === '立食用' || label.includes('立食用'));
      // ★ここだけで defurnish を切り替える（エリア出入りでは戻さない）
void setDefurnish(shouldDefurnish);
    },
    [active, setDefurnish],
  );

  // 表示アイテム（1スロットなので active が無くても0番で解決）
  const visibleItems = useMemo<ItemDesc[]>(() => {
    const target = active ?? slotsRef.current[0] ?? null;
    if (!target) return [];

    const category = slots.get(target.node.name);
    if (!category) {
      console.warn('slot に対応する category が見つかりません', target.node.name);
      return [];
    }
    return items.get(category) ?? [];
  }, [active]);

  return (
    <div className="main">
      {/* エリア内に入ったらUI表示 */}
      {inArea && <ItemList items={visibleItems} onSelected={handleItemPick} />}

      <Frame src={src} />
    </div>
  );
}
